import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kmgestao/ui';

import type { ExistingClinic } from '../../../features/leads/index.js';

export interface AlreadyClientDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Clínica encontrada — apresentada ao user com nome + data de
   * criação para contexto. */
  clinic: ExistingClinic;
}

/**
 * Dialog mostrado quando Karla tenta cadastrar um lead com CNPJ que
 * já pertence a uma **clínica-cliente ativa** (assinou contrato e
 * virou cliente). Evita "recadastro como lead" de quem já comprou.
 *
 * Não há ação remediativa aqui — o caminho correto é ir para a tela
 * da clínica (M3+) ou entrar em contato. Por ora, só informa e
 * cancela o cadastro.
 *
 * Mobile-first: Dialog do DS já trata safe-area. Botão grande
 * (size=lg, ≥44px).
 */
export function AlreadyClientDialog({ open, onOpenChange, clinic }: AlreadyClientDialogProps) {
  const displayName = clinic.trade_name ?? clinic.legal_name ?? 'Essa empresa';
  const createdAt = formatDate(clinic.created_at);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Essa empresa já é cliente</DialogTitle>
          <DialogDescription>
            O CNPJ informado pertence a uma clínica que já assinou contrato e foi ativada na
            plataforma. Não é necessário cadastrá-la como lead.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 rounded-md border border-border bg-muted/50 p-4">
          <p className="text-sm font-medium text-foreground sm:text-base">{displayName}</p>
          {createdAt ? (
            <p className="text-xs text-muted-foreground">Cliente desde {createdAt}</p>
          ) : null}
        </div>

        <p className="text-xs text-muted-foreground">
          Se precisar acessar informações dessa clínica, use a área de clínicas-cliente (em breve no
          menu Superadmin).
        </p>

        <DialogFooter>
          <DialogClose asChild>
            <Button size="lg" className="w-full sm:w-auto">
              Entendido
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string): string | null {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}
